import { Modal } from 'antd'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import AtomSearchInput from '@/components/Atoms/AtomSearchInput/AtomSearchInput'
import AtomTable from '@/components/Atoms/AtomTable/AtomTable'
import Loader from '@/components/Atoms/Loader/Loader'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import {
  PartnershipModel,
  SupplierDirectoryItemDto,
  SupplierDirectoryResponse,
} from '@/types'
import api from '@/utils/api/api'
import { handleApiError } from '@/utils/api/errors/handleApiError'

import InviteModal from '../Partnerships/modals/InviteModal'
import {
  getSuppliersColumns,
  SUPPLIERS_COLUMN_WIDTHS,
} from './SuppliersTab.config'
import cn from './SuppliersTab.module.scss'

interface Props {
  onPartnershipCreated?: () => void
}

function SuppliersTab({ onPartnershipCreated }: Props) {
  const [items, setItems] = useState<SupplierDirectoryItemDto[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [modal, contextHolder] = Modal.useModal()
  const isOrgSelected = useUserInfoStore(
    (state) => Object.keys(state.selectedOrganisation).length > 0
  )

  const [selectedItem, setSelectedItem] =
    useState<SupplierDirectoryItemDto | null>(null)
  const [inviteOpen, setInviteOpen] = useState(false)

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get<SupplierDirectoryResponse>(
        '/partnerships/directory'
      )
      setItems(res.data.items || [])
    } catch (e) {
      handleApiError(e, modal)
    } finally {
      setLoading(false)
    }
  }, [modal])

  useEffect(() => {
    loadData()
  }, [loadData])

  const openInvite = (item: SupplierDirectoryItemDto) => {
    setSelectedItem(item)
    setInviteOpen(true)
  }

  const handleInviteSuccess = (newPartnership: PartnershipModel) => {
    setInviteOpen(false)
    setSelectedItem(null)

    const partnerName = newPartnership?.partner?.description || 'Поставщик'
    toast.success(`Приглашение отправлено — ${partnerName}`)

    loadData()

    if (onPartnershipCreated) {
      onPartnershipCreated()
    }
  }

  const columns = useMemo(() => getSuppliersColumns(openInvite), [])

  const normalizedSearch = search.trim().toLowerCase()
  const filteredItems = items.filter(
    ({ supplier }) =>
      supplier.description.toLowerCase().includes(normalizedSearch) ||
      (supplier.legalAddress?.toLowerCase().includes(normalizedSearch) ?? false)
  )

  return (
    <div className={cn.suppliersTab}>
      {contextHolder}

      <div className={cn.searchBar}>
        <AtomSearchInput
          placeholder="Поиск по наименованию..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onSearch={setSearch}
          className={cn.searchInput}
          isOrganisationSelected={isOrgSelected}
        />
      </div>

      <AtomTable
        ariaLabel="Поставщики"
        data={filteredItems}
        columns={columns}
        getRowId={(item) => item.supplier.id}
        columnWidths={SUPPLIERS_COLUMN_WIDTHS}
        isOrganisationSelected={isOrgSelected}
        emptyContent={loading ? <Loader size={24} /> : undefined}
      />

      <InviteModal
        open={inviteOpen}
        preselectedSupplier={selectedItem?.supplier ?? null}
        onClose={() => {
          setInviteOpen(false)
          setSelectedItem(null)
        }}
        onSuccess={handleInviteSuccess}
        modal={modal}
      />
    </div>
  )
}

export default SuppliersTab
